import Svg, { ClipPath, G, Path, Rect } from "react-native-svg";

import { TriageSummaryContent } from "@/api/generated/models";
import { AssistantClientEvent } from "@/api/model/Conversation";
import { ChatNote, Text } from "@/components";
import { shortBrandName } from "@/config/brand";
import { captureException } from "@/sentry";
import MessageSatisfaction from "../MessageSatisfaction";

export const FirstAidIcon = ({ color }: { color: string }) => (
  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
    <G clipPath="url(#first_aid_icon_clip_path)">
      <Path
        d="M7.6 2.5V7.5C7.6 7.55523 7.55523 7.6 7.5 7.6H2.5C1.67157 7.6 0.999998 8.27157 1 9.1L1.00001 14.9C1.00001 15.7284 1.67158 16.4 2.50001 16.4H7.50001C7.55524 16.4 7.60001 16.4448 7.60001 16.5L7.6 21.5C7.6 22.3284 8.27157 23 9.1 23H14.9054C15.7338 23 16.4054 22.3284 16.4054 21.5L16.4054 16.4994C16.4054 16.4444 16.4498 16.3997 16.5048 16.3994L21.5092 16.3686C22.334 16.3636 23 15.6935 23 14.8687L23 9.06873C23 8.2367 22.3228 7.56364 21.4908 7.56876L16.506 7.59938C16.4505 7.59972 16.4054 7.55485 16.4054 7.49938V2.5C16.4054 1.67157 15.7338 1 14.9054 1H9.1C8.27157 1 7.6 1.67157 7.6 2.5Z"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </G>
    <ClipPath id="first_aid_icon_clip_path">
      <Rect width={24} height={24} fill="white" />
    </ClipPath>
  </Svg>
);

export interface TriageSummaryProps extends AssistantClientEvent {
  content: TriageSummaryContent;
  type: "triage_summary";
}

const DISCLAIMER = `${shortBrandName} is not a replacement for a clinician.`;

type TriageStaticCopy = {
  title?: string;
  subtitle?: string;
  outro: string;
};

const IN_PERSON_STATIC_COPY: TriageStaticCopy = {
  title: "Consult a clinician",
  subtitle:
    "Based on your answers, a doctor would likely recommend in-person evaluation",
  outro:
    "I can ask a few more questions to prepare a summary for your doctor and make your visit more efficient. Would you like to continue?",
};

const getStaticCopyByDisposition = (): Record<
  "Virtual visit" | "Emergency room" | "In person PCP" | "Urgent Care",
  TriageStaticCopy
> => ({
  "Virtual visit": {
    outro:
      "After a few more questions, I'll be able to connect you to a clinician who can address your need. Otherwise, I'll prepare a summary that you can take a doctor in person. Which do you prefer?",
  },
  "Emergency room": {
    title: "Seek care immediately",
    subtitle:
      "Based on your answers, you may need urgent medical attention. Go to the nearest emergency room or call 911.",
    outro: "",
  },
  "In person PCP": IN_PERSON_STATIC_COPY,
  "Urgent Care": IN_PERSON_STATIC_COPY,
});

export default function TriageSummary({
  content,
  messageId,
  feedback,
  feedbackComment,
  maybeCopy,
}: TriageSummaryProps) {
  // Don't render anything for Virtual visit
  if (content.disposition === "Virtual visit") {
    return null;
  }
  // Don't render anything for Home care
  if (content.disposition === "Home care") {
    return null;
  }

  const STATIC_COPY_BY_DISPOSITION = getStaticCopyByDisposition();

  if (!(content.disposition! in STATIC_COPY_BY_DISPOSITION)) {
    captureException("Unexpected disposition sent to frontend", {
      extra: { disposition: content.disposition },
    });
    return (
      <Text fontFamily="$serif">
        I recommend you seek care through {content.disposition?.toLowerCase()}
      </Text>
    );
  }

  const copy =
    STATIC_COPY_BY_DISPOSITION[
      content.disposition as keyof typeof STATIC_COPY_BY_DISPOSITION
    ];

  return (
    <>
      <ChatNote
        Icon={FirstAidIcon}
        title={copy.title}
        subtitle={copy.subtitle}
        disclaimer={DISCLAIMER}
      />
      {!!copy.outro && <Text fontFamily="$serif">{copy.outro}</Text>}
      <MessageSatisfaction
        messageId={messageId}
        feedback={feedback}
        feedbackComment={feedbackComment}
        onCopy={() =>
          maybeCopy?.(
            [copy.title, copy.subtitle, DISCLAIMER, copy.outro]
              .filter(Boolean)
              .join("\n\n"),
          )
        }
      />
    </>
  );
}
